import React, { useEffect, useMemo, useRef, useState } from "react";
import { authHeaders, rootUrl } from "../../helpers";
import {
  loadGoogleMaps,
  hasGoogleMapsApiKey,
  buildBboxFromGoogleBounds,
  plotDevStatusColor,
} from "../../helpers/googleMaps";
import {
  FaSpinner,
  FaSyncAlt,
  FaMapMarkedAlt,
  FaSearch,
  FaTimes,
  FaLayerGroup,
  FaLocationArrow,
  FaRoute,
  FaRuler,
} from "react-icons/fa";

const DEV_STATUS_OPTIONS = [
  "",
  "Complete",
  "Incomplete",
  "Undeveloped",
  "DEVELOPED",
  "Fully Developed",
];

const ZONAL_USE_OPTIONS = ["", "Residential", "Commercial", "Wall Fence"];

function isInRange(n, min, max) {
  return typeof n === "number" && n >= min && n <= max;
}

function normalizeGeometryToBbox(geometry, bboxArr) {
  // bboxArr = [xmin, ymin, xmax, ymax] where x=lon, y=lat
  if (!geometry || !bboxArr || bboxArr.length !== 4) return geometry;
  const [xmin, ymin, xmax, ymax] = bboxArr.map(Number);
  if (![xmin, ymin, xmax, ymax].every((v) => Number.isFinite(v))) return geometry;

  const coordLooksCorrect = (coord) => {
    if (!Array.isArray(coord) || coord.length < 2) return false;
    const x = Number(coord[0]);
    const y = Number(coord[1]);
    return isInRange(x, xmin, xmax) && isInRange(y, ymin, ymax);
  };

  const coordLooksSwapped = (coord) => {
    if (!Array.isArray(coord) || coord.length < 2) return false;
    const x = Number(coord[0]);
    const y = Number(coord[1]);
    return isInRange(x, ymin, ymax) && isInRange(y, xmin, xmax);
  };

  const isSaneLonLat = (lon, lat) =>
    Number.isFinite(lon) && Number.isFinite(lat) && Math.abs(lon) <= 180 && Math.abs(lat) <= 90;

  const firstCoordsSample = (coords, depth, out, limit = 40) => {
    if (!Array.isArray(coords) || out.length >= limit) return;
    if (depth === 0) {
      out.push(coords);
      return;
    }
    for (const c of coords) {
      if (out.length >= limit) break;
      firstCoordsSample(c, depth - 1, out, limit);
    }
  };

  const decideSwapForGeometry = (coords, depth) => {
    const sample = [];
    firstCoordsSample(coords, depth, sample);
    if (!sample.length) return false;
    let correct = 0;
    let swapped = 0;
    for (const c of sample) {
      if (coordLooksCorrect(c)) correct++;
      else if (coordLooksSwapped(c)) swapped++;
    }
    return swapped > correct;
  };

  const mapCoords = (coords, depth, swapAll) => {
    if (!Array.isArray(coords)) return coords;
    if (depth === 0) {
      const rawX = Number(coords[0]);
      const rawY = Number(coords[1]);
      const [lon, lat] = swapAll ? [rawY, rawX] : [rawX, rawY];
      if (!isSaneLonLat(lon, lat)) return null;
      return [lon, lat];
    }
    return coords
      .map((c) => mapCoords(c, depth - 1, swapAll))
      .filter((v) => v !== null);
  };

  if (geometry.type === "Polygon") {
    const swapAll = decideSwapForGeometry(geometry.coordinates, 2);
    const coords = mapCoords(geometry.coordinates, 2, swapAll);
    const closed = (coords || []).map((ring) => {
      if (!Array.isArray(ring) || ring.length < 3) return ring;
      const first = ring[0];
      const last = ring[ring.length - 1];
      if (first && last && first[0] === last[0] && first[1] === last[1]) return ring;
      return [...ring, first];
    });
    return { ...geometry, coordinates: closed };
  }
  if (geometry.type === "MultiPolygon") {
    const swapAll = decideSwapForGeometry(geometry.coordinates, 3);
    const coords = mapCoords(geometry.coordinates, 3, swapAll);
    const closed = (coords || []).map((poly) =>
      (poly || []).map((ring) => {
        if (!Array.isArray(ring) || ring.length < 3) return ring;
        const first = ring[0];
        const last = ring[ring.length - 1];
        if (first && last && first[0] === last[0] && first[1] === last[1]) return ring;
        return [...ring, first];
      })
    );
    return { ...geometry, coordinates: closed };
  }
  if (geometry.type === "LineString") {
    const swapAll = decideSwapForGeometry(geometry.coordinates, 1);
    return { ...geometry, coordinates: mapCoords(geometry.coordinates, 1, swapAll) };
  }
  if (geometry.type === "MultiLineString") {
    const swapAll = decideSwapForGeometry(geometry.coordinates, 2);
    return { ...geometry, coordinates: mapCoords(geometry.coordinates, 2, swapAll) };
  }
  if (geometry.type === "Point") {
    const swapAll = decideSwapForGeometry([geometry.coordinates], 1);
    const mapped = mapCoords(geometry.coordinates, 0, swapAll);
    return mapped ? { ...geometry, coordinates: mapped } : geometry;
  }
  if (geometry.type === "MultiPoint") {
    const swapAll = decideSwapForGeometry(geometry.coordinates, 1);
    return { ...geometry, coordinates: mapCoords(geometry.coordinates, 1, swapAll) };
  }
  return geometry;
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
}

const DEFAULT_CENTER = { lat: -15.4167, lng: 28.2833 }; // Lusaka fallback
const USER_LOCATION_ZOOM = 16;

function getCurrentPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported by your browser"));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 12000,
      maximumAge: 60000,
    });
  });
}

function getPlotDestination(props) {
  const lat = Number(props?.centroid_lat);
  const lng = Number(props?.centroid_lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

function formatDistanceKm(meters) {
  if (!Number.isFinite(meters)) return "—";
  const km = meters / 1000;
  if (km < 1) return `${Math.round(meters)} m`;
  return `${km.toFixed(km < 10 ? 2 : 1)} km`;
}

function formatDuration(seconds) {
  if (!Number.isFinite(seconds)) return "—";
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

export default function LandManagement() {
  const mapElRef = useRef(null);
  const mapRef = useRef(null);
  const infoWindowRef = useRef(null);
  const userMarkerRef = useRef(null);
  const userPositionRef = useRef(null);
  const directionsServiceRef = useRef(null);
  const directionsRendererRef = useRef(null);
  const lastReqAbortRef = useRef(null);
  const cacheRef = useRef(new Map());
  const listenersRef = useRef([]);

  const [mapsReady, setMapsReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [locationStatus, setLocationStatus] = useState("");
  const [err, setErr] = useState("");
  const [geojson, setGeojson] = useState(null);
  const [selected, setSelected] = useState(null);
  const [distanceInfo, setDistanceInfo] = useState(null);
  const [routeActive, setRouteActive] = useState(false);
  const [distanceLoading, setDistanceLoading] = useState(false);
  const [routeLoading, setRouteLoading] = useState(false);
  const [travelMode, setTravelMode] = useState("DRIVING");
  const [routeErr, setRouteErr] = useState("");

  const [filtersOpen, setFiltersOpen] = useState(false);
  const selectedPanelRef = useRef(null);

  const [filters, setFilters] = useState({
    dev_status: "",
    zonal_use: "",
    limit: 500,
    search: "",
  });

  const filteredFeatures = useMemo(() => {
    const feats = geojson?.features || [];
    const s = filters.search.trim().toLowerCase();
    if (!s) return feats;
    return feats.filter((f) => {
      const p = f?.properties || {};
      return (
        String(p.plot_num || "").toLowerCase().includes(s) ||
        String(p.city || "").toLowerCase().includes(s) ||
        String(p.location || "").toLowerCase().includes(s) ||
        String(p.dev_status || "").toLowerCase().includes(s) ||
        String(p.zonal_use || "").toLowerCase().includes(s)
      );
    });
  }, [geojson, filters.search]);

  const clearRoute = () => {
    if (directionsRendererRef.current) {
      directionsRendererRef.current.setMap(null);
    }
    setRouteActive(false);
    setRouteErr("");
  };

  const clearDistanceAndRoute = () => {
    clearRoute();
    setDistanceInfo(null);
    setRouteErr("");
  };

  const ensureUserPosition = async () => {
    if (userPositionRef.current) return userPositionRef.current;
    const pos = await getCurrentPosition();
    const position = {
      lat: pos.coords.latitude,
      lng: pos.coords.longitude,
    };
    userPositionRef.current = position;
    const map = mapRef.current;
    if (map) placeUserMarker(map, position);
    return position;
  };

  const requestDirections = (origin, destination, mode) =>
    new Promise((resolve, reject) => {
      if (!window.google?.maps) {
        reject(new Error("Google Maps is not ready"));
        return;
      }
      if (!directionsServiceRef.current) {
        directionsServiceRef.current = new window.google.maps.DirectionsService();
      }
      const travel =
        window.google.maps.TravelMode[mode] || window.google.maps.TravelMode.DRIVING;

      directionsServiceRef.current.route(
        {
          origin,
          destination,
          travelMode: travel,
          provideRouteAlternatives: false,
        },
        (result, status) => {
          if (status === "OK" && result) resolve(result);
          else {
            const messages = {
              ZERO_RESULTS: "No route found between your location and this plot.",
              NOT_FOUND: "Could not find a route for one of the locations.",
              OVER_QUERY_LIMIT: "Directions quota exceeded. Try again later.",
              REQUEST_DENIED:
                "Directions request denied. Enable the Directions API for your Google Maps key.",
              INVALID_REQUEST: "Invalid directions request.",
            };
            reject(new Error(messages[status] || `Directions failed (${status})`));
          }
        }
      );
    });

  const viewDistance = async () => {
    const dest = getPlotDestination(selected?.properties);
    if (!dest) {
      setRouteErr("This plot has no centroid coordinates for distance.");
      return;
    }
    setDistanceLoading(true);
    setRouteErr("");
    try {
      const origin = await ensureUserPosition();
      const result = await requestDirections(origin, dest, travelMode);
      const leg = result.routes?.[0]?.legs?.[0];
      if (!leg) throw new Error("No distance data returned");
      setDistanceInfo({
        distanceText: leg.distance?.text || formatDistanceKm(leg.distance?.value),
        distanceMeters: leg.distance?.value,
        durationText: leg.duration?.text || formatDuration(leg.duration?.value),
        durationSeconds: leg.duration?.value,
        mode: travelMode,
        plotLabel: selected?.properties?.plot_num || "Plot",
      });
    } catch (e) {
      setDistanceInfo(null);
      setRouteErr(e?.message || "Failed to get distance");
    } finally {
      setDistanceLoading(false);
    }
  };

  const showRoute = async () => {
    const map = mapRef.current;
    const dest = getPlotDestination(selected?.properties);
    if (!map) return;
    if (!dest) {
      setRouteErr("This plot has no centroid coordinates for routing.");
      return;
    }
    setRouteLoading(true);
    setRouteErr("");
    try {
      const origin = await ensureUserPosition();
      const result = await requestDirections(origin, dest, travelMode);

      if (!directionsRendererRef.current) {
        directionsRendererRef.current = new window.google.maps.DirectionsRenderer({
          suppressMarkers: false,
          preserveViewport: false,
          polylineOptions: {
            strokeColor: "#2563eb",
            strokeOpacity: 0.9,
            strokeWeight: 5,
          },
        });
      }
      directionsRendererRef.current.setMap(map);
      directionsRendererRef.current.setDirections(result);
      setRouteActive(true);

      const leg = result.routes?.[0]?.legs?.[0];
      if (leg) {
        setDistanceInfo({
          distanceText: leg.distance?.text || formatDistanceKm(leg.distance?.value),
          distanceMeters: leg.distance?.value,
          durationText: leg.duration?.text || formatDuration(leg.duration?.value),
          durationSeconds: leg.duration?.value,
          mode: travelMode,
          plotLabel: selected?.properties?.plot_num || "Plot",
        });
      }
    } catch (e) {
      clearRoute();
      setRouteErr(e?.message || "Failed to show route");
    } finally {
      setRouteLoading(false);
    }
  };

  const placeUserMarker = (map, position) => {
    if (!map || !window.google?.maps) return;

    userPositionRef.current = position;

    if (userMarkerRef.current) {
      userMarkerRef.current.setMap(null);
      userMarkerRef.current = null;
    }

    const marker = new window.google.maps.Marker({
      position,
      map,
      title: "Your location",
      zIndex: 9999,
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 9,
        fillColor: "#2563eb",
        fillOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: 3,
      },
    });

    marker.addListener("click", () => {
      if (!infoWindowRef.current) return;
      infoWindowRef.current.setContent(
        `<div style="padding:4px 2px;font-size:13px;"><strong>Your location</strong><div style="color:#64748b;margin-top:2px">${position.lat.toFixed(6)}, ${position.lng.toFixed(6)}</div></div>`
      );
      infoWindowRef.current.open(map, marker);
    });

    userMarkerRef.current = marker;
  };

  const centerOnUserLocation = async ({ silent = false } = {}) => {
    const map = mapRef.current;
    if (!map) return;

    setLocating(true);
    if (!silent) setLocationStatus("Getting your location…");

    try {
      const pos = await getCurrentPosition();
      const position = {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
      };
      placeUserMarker(map, position);
      map.panTo(position);
      map.setZoom(USER_LOCATION_ZOOM);
      setLocationStatus("Showing your current location");
    } catch (e) {
      const message =
        e?.code === 1
          ? "Location permission denied — showing default area"
          : e?.message || "Could not get your location — showing default area";
      setLocationStatus(message);
      if (!userMarkerRef.current) {
        map.setCenter(DEFAULT_CENTER);
        map.setZoom(12);
      }
    } finally {
      setLocating(false);
    }
  };

  const fetchPlots = async ({ bbox, dev_status, zonal_use, limit }) => {
    setErr("");
    const key = JSON.stringify({ bbox, dev_status, zonal_use, limit });
    if (cacheRef.current.has(key)) {
      setGeojson(cacheRef.current.get(key));
      return;
    }

    if (lastReqAbortRef.current) lastReqAbortRef.current.abort();
    const controller = new AbortController();
    lastReqAbortRef.current = controller;

    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("bbox", bbox);
      if (dev_status) params.set("dev_status", dev_status);
      if (zonal_use) params.set("zonal_use", zonal_use);
      if (limit) params.set("limit", String(limit));

      const res = await fetch(rootUrl(`/plots?${params.toString()}`), {
        signal: controller.signal,
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || "Failed to load plots");
      }
      cacheRef.current.set(key, data);
      setGeojson(data);
    } catch (e) {
      if (e?.name === "AbortError") return;
      setErr(e?.message || "Failed to load plots");
      setGeojson(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshFromMap = useMemo(
    () =>
      debounce(() => {
        const map = mapRef.current;
        if (!map) return;
        const bounds = map.getBounds();
        if (!bounds) return;
        const bbox = buildBboxFromGoogleBounds(bounds);
        fetchPlots({
          bbox,
          dev_status: filters.dev_status,
          zonal_use: filters.zonal_use,
          limit: filters.limit,
        });
      }, 350),
    [filters.dev_status, filters.zonal_use, filters.limit]
  );

  useEffect(() => {
    let mounted = true;
    if (!hasGoogleMapsApiKey()) {
      setErr(
        "Missing Google Maps API key. Set REACT_APP_GOOGLE_MAPS_API_KEY in .env (see .env.example), then restart the app."
      );
      return;
    }
    loadGoogleMaps()
      .then(() => mounted && setMapsReady(true))
      .catch((e) => mounted && setErr(e?.message || "Failed to load map"));
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!mapsReady) return;
    if (!mapElRef.current) return;
    if (mapRef.current) return;

    const map = new window.google.maps.Map(mapElRef.current, {
      center: DEFAULT_CENTER,
      zoom: 12,
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: window.google.maps.MapTypeControlStyle.DROPDOWN_MENU,
        mapTypeIds: ["roadmap", "satellite", "hybrid", "terrain"],
      },
      streetViewControl: false,
      fullscreenControl: true,
      mapTypeId: "roadmap",
    });

    infoWindowRef.current = new window.google.maps.InfoWindow();

    map.data.setStyle((feature) => {
      const status = feature.getProperty("dev_status");
      return {
        strokeColor: "#111827",
        strokeWeight: 3,
        strokeOpacity: 0.95,
        fillColor: plotDevStatusColor(status),
        fillOpacity: 0.55,
      };
    });

    const onClick = map.data.addListener("click", (event) => {
      const feature = event.feature;
      const props = {};
      feature.forEachProperty((value, key) => {
        props[key] = value;
      });
      clearDistanceAndRoute();
      setSelected({ properties: props, geometry: null });
      setTimeout(() => {
        selectedPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 50);

      const title = `${props.plot_num || "Plot"}${props.location ? ` • ${props.location}` : ""}`;
      infoWindowRef.current.setContent(
        `<div style="padding:4px 2px;font-size:13px;"><strong>${title}</strong></div>`
      );
      infoWindowRef.current.setPosition(event.latLng);
      infoWindowRef.current.open(map);
    });

    const onMouseOver = map.data.addListener("mouseover", (event) => {
      map.data.overrideStyle(event.feature, {
        strokeWeight: 5,
        strokeColor: "#ffffff",
        strokeOpacity: 1,
        fillOpacity: 0.7,
      });
    });

    const onMouseOut = map.data.addListener("mouseout", (event) => {
      map.data.revertStyle(event.feature);
    });

    const onIdle = map.addListener("idle", refreshFromMap);

    listenersRef.current = [onClick, onMouseOver, onMouseOut, onIdle];
    mapRef.current = map;

    // Default: show the user's current location, then load nearby plots.
    centerOnUserLocation({ silent: true }).finally(() => {
      refreshFromMap();
    });

    return () => {
      listenersRef.current.forEach((l) => {
        if (l) window.google.maps.event.removeListener(l);
      });
      listenersRef.current = [];
      if (directionsRendererRef.current) {
        directionsRendererRef.current.setMap(null);
        directionsRendererRef.current = null;
      }
      if (userMarkerRef.current) {
        userMarkerRef.current.setMap(null);
        userMarkerRef.current = null;
      }
      if (infoWindowRef.current) infoWindowRef.current.close();
      mapRef.current = null;
    };
  }, [mapsReady, refreshFromMap]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.data.forEach((feature) => map.data.remove(feature));
    if (!filteredFeatures.length) return;

    const bboxArr = Array.isArray(geojson?.bbox) ? geojson.bbox : null;
    const normalized = {
      type: "FeatureCollection",
      features: filteredFeatures.map((f) => ({
        ...f,
        geometry: normalizeGeometryToBbox(f.geometry, bboxArr),
      })),
    };
    map.data.addGeoJson(normalized);
  }, [geojson, filteredFeatures]);

  const zoomToFeature = (feature) => {
    const map = mapRef.current;
    if (!map || !feature) return;
    const p = feature.properties || {};
    clearDistanceAndRoute();
    setSelected(feature);
    setTimeout(() => {
      selectedPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 50);

    const cLat = Number(p.centroid_lat);
    const cLon = Number(p.centroid_lon);
    if (Number.isFinite(cLat) && Number.isFinite(cLon)) {
      map.panTo({ lat: cLat, lng: cLon });
      map.setZoom(Math.max(map.getZoom() || 12, 17));
      if (infoWindowRef.current) {
        const title = `${p.plot_num || "Plot"}${p.location ? ` • ${p.location}` : ""}`;
        infoWindowRef.current.setContent(
          `<div style="padding:4px 2px;font-size:13px;"><strong>${title}</strong></div>`
        );
        infoWindowRef.current.setPosition({ lat: cLat, lng: cLon });
        infoWindowRef.current.open(map);
      }
    }
  };

  const onClearFilters = () => {
    setFilters({ dev_status: "", zonal_use: "", limit: 500, search: "" });
    cacheRef.current.clear();
    setSelected(null);
    setGeojson(null);
    setErr("");
    clearDistanceAndRoute();
    setTimeout(() => refreshFromMap(), 0);
  };

  const selectedProps = selected?.properties || null;

  return (
    <div className="w-full h-full min-h-[calc(100vh-8rem)] flex flex-col bg-slate-100 overflow-hidden">
      {/* Top toolbar */}
      <div className="flex-shrink-0 px-3 py-2.5 bg-white border-b border-gray-200 shadow-sm z-20">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
              <FaMapMarkedAlt className="text-blue-700" />
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-gray-900 truncate">GIS Map</div>
              <div className="text-xs text-gray-500 truncate">
                Click a plot on the map, then use Distance / Route in the panel.
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[12rem] max-w-xs">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
              <input
                className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 text-sm bg-white"
                value={filters.search}
                placeholder="Search plots in view…"
                onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
              />
            </div>
            <button
              type="button"
              onClick={() => setFiltersOpen((v) => !v)}
              className={`px-3 py-2 rounded-lg text-sm flex items-center gap-2 border ${
                filtersOpen
                  ? "bg-blue-50 border-blue-300 text-blue-800"
                  : "bg-white border-gray-300 text-gray-800 hover:bg-gray-50"
              }`}
            >
              <FaLayerGroup />
              Filters
            </button>
            <button
              onClick={() => centerOnUserLocation()}
              className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm flex items-center gap-2 hover:bg-blue-700 disabled:opacity-60"
              type="button"
              disabled={locating || !mapsReady}
            >
              {locating ? <FaSpinner className="animate-spin" /> : <FaLocationArrow />}
              My location
            </button>
            <button
              onClick={() => refreshFromMap()}
              className="px-3 py-2 rounded-lg bg-gray-900 text-white text-sm flex items-center gap-2 hover:bg-gray-800"
              type="button"
            >
              {loading ? <FaSpinner className="animate-spin" /> : <FaSyncAlt />}
              Refresh
            </button>
          </div>
        </div>

        {filtersOpen ? (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 p-3 rounded-xl bg-slate-50 border border-slate-200">
            <label className="text-sm">
              <div className="text-gray-700 mb-1">Development status</div>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white"
                value={filters.dev_status}
                onChange={(e) => {
                  setFilters((p) => ({ ...p, dev_status: e.target.value }));
                  cacheRef.current.clear();
                  setTimeout(() => refreshFromMap(), 0);
                }}
              >
                {DEV_STATUS_OPTIONS.map((v) => (
                  <option key={v || "__any"} value={v}>
                    {v ? v : "Any"}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <div className="text-gray-700 mb-1">Zonal use</div>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white"
                value={filters.zonal_use}
                onChange={(e) => {
                  setFilters((p) => ({ ...p, zonal_use: e.target.value }));
                  cacheRef.current.clear();
                  setTimeout(() => refreshFromMap(), 0);
                }}
              >
                {ZONAL_USE_OPTIONS.map((v) => (
                  <option key={v || "__any"} value={v}>
                    {v ? v : "Any"}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <div className="text-gray-700 mb-1">Limit</div>
              <input
                type="number"
                min={1}
                max={2000}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white"
                value={filters.limit}
                onChange={(e) => {
                  const v = Math.max(1, Math.min(2000, Number(e.target.value || 500)));
                  setFilters((p) => ({ ...p, limit: v }));
                  cacheRef.current.clear();
                  setTimeout(() => refreshFromMap(), 0);
                }}
              />
            </label>
            <div className="flex items-end gap-2">
              <button
                onClick={onClearFilters}
                className="w-full px-3 py-2 rounded-lg bg-white border border-gray-300 text-gray-900 text-sm flex items-center justify-center gap-2 hover:bg-gray-50"
                type="button"
              >
                <FaTimes />
                Reset filters
              </button>
            </div>
            <div className="sm:col-span-2 lg:col-span-4 flex items-center justify-between text-sm text-gray-600">
              <span>
                <span className="font-semibold text-gray-900">{filteredFeatures.length}</span> visible
                {geojson?.count != null ? <span className="text-gray-400"> • {geojson.count} loaded</span> : null}
              </span>
              <span className="text-xs text-gray-500">
                {locationStatus || (mapsReady ? "Map ready" : "Loading map…")}
              </span>
            </div>
          </div>
        ) : null}

        {filters.search.trim() ? (
          <div className="mt-2 max-h-36 overflow-auto border border-gray-200 rounded-lg divide-y divide-gray-100 bg-white">
            {filteredFeatures.length === 0 ? (
              <div className="p-3 text-sm text-gray-500">No matching plots in this map view.</div>
            ) : (
              filteredFeatures.slice(0, 25).map((f, idx) => {
                const p = f?.properties || {};
                const key = `${p.plot_num || "plot"}-${idx}`;
                return (
                  <button
                    key={key}
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50"
                    onClick={() => zoomToFeature(f)}
                  >
                    <div className="font-medium text-gray-900">{p.plot_num || "Plot"}</div>
                    <div className="text-xs text-gray-500 truncate">
                      {[p.location, p.city, p.dev_status].filter(Boolean).join(" • ") || "—"}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        ) : null}

        {err ? (
          <div className="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
            {err}
          </div>
        ) : null}
      </div>

      {/* Map + floating plot panel */}
      <div className="relative flex-1 min-h-0">
        <div ref={mapElRef} className="absolute inset-0 w-full h-full" />

        {!selectedProps ? (
          <div className="absolute left-3 bottom-3 z-10 max-w-sm rounded-xl bg-white/95 backdrop-blur border border-gray-200 shadow-lg px-4 py-3 text-sm text-gray-600">
            <div className="font-medium text-gray-900 mb-0.5">Select a plot</div>
            Click a colored polygon on the map to view details, distance, and route.
          </div>
        ) : (
          <div
            ref={selectedPanelRef}
            className="absolute z-10 left-3 right-3 sm:left-auto sm:right-3 sm:w-[22rem] top-3 max-h-[calc(100%-1.5rem)] overflow-y-auto rounded-2xl bg-white shadow-2xl border border-gray-200"
          >
            <div className="sticky top-0 bg-gradient-to-r from-blue-950 to-blue-800 text-white px-4 py-3 flex items-start justify-between gap-2 rounded-t-2xl">
              <div className="min-w-0">
                <div className="text-xs text-blue-200 uppercase tracking-wide">Selected plot</div>
                <div className="font-semibold text-lg truncate">{selectedProps.plot_num || "Plot"}</div>
                <div className="text-sm text-blue-100 truncate">
                  {selectedProps.location || selectedProps.city || "—"}
                </div>
              </div>
              <button
                type="button"
                className="p-1.5 rounded-lg bg-white/15 hover:bg-white/25"
                onClick={() => {
                  setSelected(null);
                  clearDistanceAndRoute();
                }}
                title="Close"
              >
                <FaTimes />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="p-2 rounded-lg bg-gray-50 border border-gray-200">
                  <div className="text-xs text-gray-500">Status</div>
                  <div className="font-medium text-gray-900">{selectedProps.dev_status || "—"}</div>
                </div>
                <div className="p-2 rounded-lg bg-gray-50 border border-gray-200">
                  <div className="text-xs text-gray-500">Zonal use</div>
                  <div className="font-medium text-gray-900">{selectedProps.zonal_use || "—"}</div>
                </div>
                <div className="p-2 rounded-lg bg-gray-50 border border-gray-200">
                  <div className="text-xs text-gray-500">Structures</div>
                  <div className="font-medium text-gray-900">
                    {selectedProps.num_struct != null ? selectedProps.num_struct : "—"}
                  </div>
                </div>
                <div className="p-2 rounded-lg bg-gray-50 border border-gray-200">
                  <div className="text-xs text-gray-500">Survey date</div>
                  <div className="font-medium text-gray-900">{selectedProps.survey_date || "—"}</div>
                </div>
              </div>

              <button
                type="button"
                className="w-full px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm"
                onClick={() => {
                  const map = mapRef.current;
                  if (!map) return;
                  const dest = getPlotDestination(selectedProps);
                  if (dest) {
                    map.panTo(dest);
                    map.setZoom(Math.max(map.getZoom() || 12, 16));
                  }
                }}
              >
                Zoom to plot
              </button>

              <div className="rounded-xl border-2 border-indigo-200 bg-indigo-50/60 p-3 space-y-3">
                <div className="font-semibold text-indigo-950 text-sm">Distance & route</div>
                <p className="text-xs text-indigo-800/80">
                  Optional — only runs when you click a button below (uses Directions API).
                </p>

                <label className="block text-sm">
                  <div className="text-gray-700 mb-1">Travel mode</div>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white"
                    value={travelMode}
                    onChange={(e) => setTravelMode(e.target.value)}
                  >
                    <option value="DRIVING">Driving</option>
                    <option value="WALKING">Walking</option>
                    <option value="BICYCLING">Bicycling</option>
                    <option value="TRANSIT">Transit</option>
                  </select>
                </label>

                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    className="px-3 py-2.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 flex items-center justify-center gap-2 text-sm font-medium"
                    onClick={viewDistance}
                    disabled={distanceLoading || routeLoading}
                  >
                    {distanceLoading ? <FaSpinner className="animate-spin" /> : <FaRuler />}
                    Distance
                  </button>
                  <button
                    type="button"
                    className="px-3 py-2.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60 flex items-center justify-center gap-2 text-sm font-medium"
                    onClick={showRoute}
                    disabled={distanceLoading || routeLoading}
                  >
                    {routeLoading ? <FaSpinner className="animate-spin" /> : <FaRoute />}
                    Show route
                  </button>
                </div>

                {(routeActive || distanceInfo) && (
                  <button
                    type="button"
                    className="w-full px-3 py-2 rounded-lg bg-white border border-gray-300 text-gray-800 hover:bg-gray-50 flex items-center justify-center gap-2 text-sm"
                    onClick={clearDistanceAndRoute}
                  >
                    <FaTimes />
                    Clear
                  </button>
                )}

                {distanceInfo ? (
                  <div className="p-3 rounded-lg bg-white border border-indigo-200 text-sm">
                    <div className="font-semibold text-indigo-950">To {distanceInfo.plotLabel}</div>
                    <div className="mt-1 text-gray-800">
                      Distance: <span className="font-medium">{distanceInfo.distanceText}</span>
                    </div>
                    <div className="text-gray-800">
                      ETA: <span className="font-medium">{distanceInfo.durationText}</span>
                    </div>
                    <div className="text-xs text-indigo-700 mt-1 capitalize">
                      {String(distanceInfo.mode || "").toLowerCase()}
                      {routeActive ? " • route on map" : ""}
                    </div>
                  </div>
                ) : null}

                {routeErr ? (
                  <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
                    {routeErr}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
